import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider, ColorSchemeScript, Modal, mergeThemeOverrides, rem } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
// Stylesheet order is load-bearing and must stay exactly like this:
//   1. Mantine's own CSS (core first — it declares the `--mantine-*` vars the
//      rest of the stack overrides; the add-on packages extend it).
//   2. The MTB design tokens.
//   3. The bridge, which repoints Mantine's variables at those tokens. It only
//      wins because it comes after every Mantine stylesheet.
//   4. App styles last, so the app can still override both.
import '@mantine/core/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';
import '@mtb/theme/styles.css';
import '@mtb/theme/mantine-bridge.css';
import './styles/print.css';
import './styles/ui-prefs.css';
import { router } from './router';
import { theme } from './theme';
import { initObservability } from './lib/observability';
import { queryClient } from './lib/queryClient';
import { PwaUpdater } from './components/PwaUpdater';
import { useUi, systemColorScheme } from './lib/uiStore';

/**
 * Modal size overrides — caps the largest modal at 1000px so floor-shell
 * (1280px) and compact-laptop users never see a modal that bumps the
 * viewport edges. The default Mantine size scale is biased toward marketing
 * pages; staff-facing modals (CSV import, location switcher, edit forms)
 * read better at these tighter widths. We map the named sizes via styles
 * because Mantine's Modal exposes the size as a CSS var (`--modal-size`).
 */
const SIZE_MAP: Record<string, string> = {
  sm: rem(480),
  md: rem(640),
  lg: rem(800),
  xl: rem(1000),
};

const themeWithModalSizes = mergeThemeOverrides(theme, {
  components: {
    Modal: Modal.extend({
      defaultProps: {
        size: 'md',
      },
      styles: (_t, props) => {
        const size = typeof props.size === 'string' ? SIZE_MAP[props.size] : undefined;
        return size ? { content: { maxWidth: size } } : {};
      },
    }),
  },
});

initObservability();

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root element in index.html');

// eslint-disable-next-line react-refresh/only-export-components -- entry-point file; App must live here so the provider stack mounts inside createRoot.render
function App() {
  const colorScheme = useUi((s) => s.colorScheme);
  const schemeOverride = useUi((s) => s.schemeOverride);
  const themePreference = useUi((s) => s.themePreference);
  const reduceMotion = useUi((s) => s.reduceMotion);

  // The scheme actually painted. A mounted shell may pin it to something other
  // than the operator's device preference — today only the kiosk does, because
  // its scheme is the dealer's `kiosk_display_settings.theme` and the person
  // reading the tablet is a customer, not the operator (vendor#388). The
  // preference itself is untouched and comes back on exit.
  const appliedScheme = schemeOverride ?? colorScheme;

  // When the user picks "System", keep the applied scheme in lock-step with the
  // OS: seed it once and re-resolve whenever `prefers-color-scheme` flips.
  useEffect(() => {
    if (themePreference !== 'auto') return;
    const mql = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mql) return;
    const sync = () => useUi.getState().setColorScheme(systemColorScheme());
    sync();
    mql.addEventListener('change', sync);
    return () => mql.removeEventListener('change', sync);
  }, [themePreference]);

  // Mirror the reduce-motion preference onto <html> so styles/ui-prefs.css can
  // collapse animations app-wide.
  useEffect(() => {
    document.documentElement.dataset.reduceMotion = String(reduceMotion);
  }, [reduceMotion]);

  // Keep the design system's `data-scheme` in lock-step with Mantine's own
  // colour scheme. The MTB semantic tokens resolve per-scheme and default to
  // DARK on a bare `:root`, so this attribute is not optional — without it a
  // light Mantine theme renders on dark tokens. `index.html` seeds it to
  // "light" so first paint is correct before React mounts; this keeps it
  // honest afterwards.
  useEffect(() => {
    document.documentElement.dataset.scheme = appliedScheme;
  }, [appliedScheme]);

  return (
    <MantineProvider theme={themeWithModalSizes} forceColorScheme={appliedScheme}>
      <Notifications />
      <PwaUpdater />
      <ModalsProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </ModalsProvider>
    </MantineProvider>
  );
}

createRoot(root).render(
  <StrictMode>
    <ColorSchemeScript defaultColorScheme="light" />
    <App />
  </StrictMode>,
);
